import React, { useContext, useEffect, useMemo, useState } from "react";
import { files, server } from "../configs";
import { Context } from "../GlobalContext";
import SurveySelectThree from "../components/SurveySelectThree";
import SurveySelectFour from "../components/SurveySelectFour";
import SurveySelectFive from "../components/SurveySelectFive";
import SurveyTextArea from "../components/SurveyTextArea";
import SurveySelectRange from "../components/SurveySelectRange";
import MultipleChoice from "../components/MultipleChoice";
import ProgressBar from "../components/ProgressBar";
import Loading from "../components/Loading";
import Axios from "axios";
import Slider from "react-slick";
import Swal from "sweetalert2";
import "../global/pages.css";
import "./Survey.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Surveys = ({ match, history }) => {
  const {
    userData,
    token,
    surveyList,
    surveyLength,
    answerData,
    progressData,
    setIsProgressLoaded,
    setIsSurveyDone,
  } = useContext(Context);
  const [currentTopic, setCurrentTopic] = useState();
  const [currentScreen, setCurrentScreen] = useState({});
  const [screenIdx, setScreenIdx] = useState();
  const [answersList, setAnswersList] = useState([]);
  const [isSubmitActive, setIsSubmitActive] = useState(true);

  const form = (str) => {
    if (!str) return "";

    function _hasJong(string) {
      //string의 마지막 글자가 받침을 가지는지 확인
      string = string.charCodeAt(string.length - 1);
      return (string - 0xac00) % 28 > 0;
    }

    const r = (s) => (_hasJong(s) ? `${s}은` : `${s}는`);

    return str.includes("||")
      ? str
          .replaceAll(
            "%REGION%",
            userData?.gov?.name.includes(" // ")
              ? r(userData?.gov?.name.split(" // ")[1])
              : ""
          )
          .split("||")[1]
      : str.replaceAll(
          "%REGION%",
          userData?.gov?.name.includes(" // ")
            ? r(userData?.gov?.name.split(" // ")[1])
            : ""
        );
  };

  const surveyType = useMemo(
    () => match.params.surveyType,
    [match.params.surveyType]
  );

  const surveyNumber = useMemo(
    () => parseInt(match.params.surveyNumber),
    [match.params.surveyNumber]
  );

  const topicNumber = useMemo(
    () => parseInt(match.params.topicNumber),
    [match.params.topicNumber]
  );

  const screenNumber = useMemo(
    () => parseInt(match.params.screenNumber),
    [match.params.screenNumber]
  );

  const isFirstScreenOfSurvey = useMemo(
    () =>
      currentScreen
        ? currentScreen.id === surveyList[surveyNumber].topics[0].screens[0].id
        : null,
    [surveyNumber, surveyList, currentScreen]
  );

  const isFirstScreenOfTopic = useMemo(
    () => screenNumber === 0,
    [screenNumber]
  );

  const isLastScreenOfSurvey = useMemo(
    () =>
      currentScreen
        ? currentScreen.id ===
          surveyList[surveyNumber].topics[
            surveyList[surveyNumber].topics.length - 1
          ].screens[
            surveyList[surveyNumber].topics[
              surveyList[surveyNumber].topics.length - 1
            ].screens.length - 1
          ].id
        : null,
    [surveyList, surveyNumber, currentScreen]
  );

  const isLastScreenOfTopic = useMemo(
    () =>
      currentScreen
        ? currentScreen.id ===
          surveyList[surveyNumber].topics[topicNumber].screens[
            surveyList[surveyNumber].topics[topicNumber].screens.length - 1
          ].id
        : null,
    [currentScreen, surveyList, surveyNumber, topicNumber]
  );

  const isLastSurvey = useMemo(
    () => surveyNumber === surveyList.length - 1,
    [surveyNumber, surveyList]
  );

  const moveProgBar = useMemo(
    () => ((screenIdx ? screenIdx : 0) / surveyLength[surveyNumber]) * 100,
    [screenIdx, surveyLength, surveyNumber]
  );

  useEffect(() => {
    setAnswersList(answerData);

    // If user came back on another device/browser, send user to where they have to
    // continue responding. Only when surveyAnswers is empty to prevent blocking the
    // user from going back to check previous responses.
    if (!localStorage.getItem("surveyAnswers")) {
      loop: for (let [surveyIndex, survey] of progressData.surveys.entries()) {
        // Find out surveyType
        const surveyTypeToShow =
          surveyList[surveyIndex].surveyType === "아동척도"
            ? "a"
            : surveyList[surveyIndex].surveyType === "영향평가"
            ? "b"
            : "c";
        for (let [topicIndex, topic] of survey.topics.entries()) {
          if (topic.notAnsweredRequiredCnt !== 0) {
            history.push(
              `/token/${token}/survey/${surveyTypeToShow}/${surveyIndex}/${topicIndex}/0`
            );
            break loop;
          }
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Set to active when screen changes to stop user from pressing
    // submit multiple times
    setIsSubmitActive(true);

    if (currentScreen) {
      let index = 0;

      loop: for (let topic of surveyList[surveyNumber].topics) {
        for (let screen of topic.screens) {
          if (currentScreen.id === screen.id) {
            break loop;
          }
          index++;
        }
      }
      // screenIdx is used to show the progress bar
      setScreenIdx(index);
    }
  }, [currentScreen, surveyList, surveyNumber]);

  useEffect(() => {
    // Setting localStorage with answerData from server (only happens once)
    if (!localStorage.getItem("surveyAnswers")) {
      localStorage.setItem("surveyAnswers", JSON.stringify(answerData));
    }
  }, [answerData]);

  useEffect(() => {
    // Setting localStorage with answerData from state (happens every answersList change)
    if (answersList.length > 0) {
      localStorage.setItem("surveyAnswers", JSON.stringify(answersList));
    }
  }, [answersList]);

  useEffect(() => {
    setCurrentTopic(surveyList[surveyNumber].topics[topicNumber]);
  }, [surveyNumber, topicNumber, surveyList]);

  useEffect(() => {
    setCurrentScreen(
      surveyList[surveyNumber].topics[topicNumber].screens[screenNumber]
    );
  }, [surveyList, surveyNumber, topicNumber, screenNumber]);

  const back = () => {
    if (isFirstScreenOfSurvey) {
      history.push(`/token/${token}/intro/${surveyType}/${surveyNumber}/0/0`);
    } else if (isFirstScreenOfTopic) {
      history.push(
        `/token/${token}/survey/${surveyType}/${surveyNumber}/${
          topicNumber - 1
        }/${
          surveyList[surveyNumber].topics[topicNumber - 1].screens.length - 1
        }`
      );
    } else {
      history.push(
        `/token/${token}/survey/${surveyType}/${surveyNumber}/${topicNumber}/${
          screenNumber - 1
        }`
      );
    }
  };

  const post = () => {
    const surveyAnswers = JSON.parse(localStorage.getItem("surveyAnswers"));
    const jwt = localStorage.getItem("jwt");

    // Only POST when surveyAnswers isn't null as if the user clicks the response for
    // the final question multiple times while waiting to be sent to the next page,
    // surveyAnswers may have been removed from localStorage, resulting in a 400
    // error as the payload, surveyAnswers, is null
    if (surveyAnswers !== null) {
      Axios.post(
        `${server}/user/survey/submit`,
        { responses: surveyAnswers },
        { headers: { authorization: jwt } }
      )
        .then(() => {
          // Push after post to prevent cases where the last answer isn't saved
          if (isLastSurvey && isLastScreenOfSurvey) {
            // Change state in GlobalContext to control order and number of requests
            setIsSurveyDone(true);

            // Remove data that's on the server from localStorage to separate
            // how data is managed
            localStorage.removeItem("surveyAnswers");
          } else if (isLastScreenOfSurvey) {
            setIsProgressLoaded(false);
            history.push(
              `/token/${token}/separator/${surveyType}/${surveyNumber}/0/0`
            );
          }
        })
        .catch((e) => console.log(e));
    }
  };

  const userResponseAlert = (id, alertMsg) => {
    const buttonColor =
      surveyType === "a"
        ? "#655ad1"
        : surveyType === "b"
        ? "#01be9c"
        : "#e77e22";

    Swal.fire({
      title: "확인해 주세요!",
      text: alertMsg,
      confirmButtonText: "닫기",
      confirmButtonColor: buttonColor,
      heightAuto: false,
    }).then(() => {
      document.getElementById(id).scrollIntoView(true, {
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    });

    setIsSubmitActive(true);
  };

  const submit = () => {
    if (isSubmitActive) {
      setIsSubmitActive(false);

      // Check entire screen if all required questions have been answered
      for (let [idx, question] of currentScreen.questions.entries()) {
        console.log(question, answersList)
        const answerIdx = answersList.findIndex(
          (answer) => answer.questionId === question.id
        );

        const ansNum = answersList[answerIdx]?.answers.length;

        // console.log(answersList.filter((x) => x.answers.length === 0))
        // if(answersList.filter((x) => x.answers.length === 0).length > 0) return;

        if (question.answerRequired) {
          // Check if user satisfied maxAnsNum and minAnsNum
          // of multiple choice questions
          if (
            question.answerType === "MUL_CHOICE" &&
            ansNum !== question.minAnsNum &&
            question.maxAnsNum === question.minAnsNum &&
            question.minAnsNum !== 1
          ) {
            userResponseAlert(
              question.id,
              `"${idx + 1}. ${form(
                question?.question
              )}" 질문은 필수로 응답해주셔야 합니다. 또한, ${
                question.minAnsNum
              }개의 선택지를 골라주시기 바랍니다.`
            );
            return;
          } else if (
            question.answerType === "MUL_CHOICE" &&
            (ansNum === undefined ||
              ansNum > question.maxAnsNum ||
              ansNum < question.minAnsNum) &&
            question.maxAnsNum > 1
          ) {
            userResponseAlert(
              question.id,
              `"${idx + 1}. ${form(
                question.question
              )}" 질문은 필수로 응답해주셔야 합니다. 또한, 최소 ${
                question.minAnsNum
              }개, 최대 ${question.maxAnsNum}개의 선택지를 골라주시기 바랍니다.`
            );
            return;
          } else if (
            answersList[answerIdx]?.answers === undefined ||
            answersList[answerIdx]?.answers.length === 0 ||
            (question.answerType === "SHORT_TEXT" &&
              answersList[answerIdx].answers[0].description.length === 0)
          ) {
            if (
              question.answerType === "DEGREE" &&
              (question.numDegree > 5 || surveyType === "c")
            ) {
              userResponseAlert(
                question.id,
                `"${idx + 1}. ${form(
                  question.question
                )}" 질문은 필수로 응답해주셔야 합니다. 슬라이더를 움직여 응답해 주시기 바랍니다.`
              );
            } else {
              userResponseAlert(
                question.id,
                `"${idx + 1}. ${form(
                  question.question
                )}" 질문은 필수로 응답해주셔야 합니다.`
              );
            }
            return;
          }

          // In case a non-required multiple choice question has minAnsNum and maxAnsNum
          // that must be enforced
        } else if (
          !question.answerRequired &&
          question.answerType === "MUL_CHOICE" &&
          question.minAnsNum === question.maxAnsNum &&
          question.minAnsNum !== 1
        ) {
          // Check if user responded it to at all to check if min and max must be enforced
          if (ansNum !== question.minAnsNum && ansNum !== 0) {
            userResponseAlert(
              question.id,
              `"${idx + 1}. ${question.question}" 질문은 응답 시 ${
                question.minAnsNum
              }개의 선택지를 골라주셔야 합니다.`
            );
            return;
          }
        }

      }

      // Set timeout for 0.5 seconds for the user to see what they've selected
      setTimeout(function () {
        if (isLastScreenOfSurvey) {
          post();
        } else if (isLastScreenOfTopic) {
          post();
          history.push(
            `/token/${token}/survey/${surveyType}/${surveyNumber}/${
              topicNumber + 1
            }/0`
          );
        } else {
          history.push(
            `/token/${token}/survey/${surveyType}/${surveyNumber}/${topicNumber}/${
              screenNumber + 1
            }`
          );
        }
      }, 500);
    }
  };

  return currentTopic === undefined || currentScreen.questions === undefined ? (
    <Loading />
  ) : (
    <div className="container" id={`survey__container__${surveyType}`}>
      <div className="survey-inner" key={`screen-${currentScreen.id}`}>
        <img
          src={`${files}/kids-${surveyType}.svg`}
          alt={surveyList[surveyNumber].name}
          className="user-img"
        />
        <h1>
          {surveyList[surveyNumber].name
            .replace("아동척도", "아동친화도시 조사\nPART1")
            .replace("도시척도", "아동친화도시 조사\nPART2")}
        </h1>
        {/* <h1>아동친화도시 조사</h1> */}
        <ProgressBar moveProgBar={moveProgBar} />
        <div className="survey-topic">
          <h2>{currentTopic.title}</h2>
        </div>
        {currentTopic.images.length !== 0 ? (
          <Slider
            dots={true}
            infinite={true}
            slidesToShow={1}
            speed={500}
            slidesToScroll={1}
            adaptiveHeight={true}
            nextArrow={<NextArrow />}
            prevArrow={<PrevArrow />}
          >
            {currentTopic.images.map(({ id, url }) => (
              <div className="subject-image" key={id}>
                <img src={url} alt={currentTopic.title} />
              </div>
            ))}
          </Slider>
        ) : null}
        {currentTopic.content && (
          <p className="topic-content">{currentTopic.content}</p>
        )}
        {currentScreen.questions.map((question, idx) => (
          <div id={question.id} key={question.id}>
            <p
              className="survey-quest"
              // data-code={
              //   question.question.includes("||")
              //     ? form(question.question)
              //     : "NONE"
              // }
            >
              {currentScreen.questions.length !== 1 ? `${idx + 1}.` : null}
              {form(question?.question)}
            </p>
            <div className="survey-quest-div">
              <div className="ans-div">
                {/* 추가문항이 surveyType c 인데, 추가문항의 척도형은 전부 게이지 형태로 나와야 함 */}
                {question.answerType === "DEGREE" &&
                  question.numDegree === 3 &&
                  surveyType !== "c" && (
                    <SurveySelectThree
                      id={question.id}
                      setAnswersList={setAnswersList}
                      answersList={answersList}
                      isSoleQuestion={currentScreen.questions.length === 1}
                      submit={submit}
                    />
                  )}
                {question.answerType === "DEGREE" &&
                  question.numDegree === 4 &&
                  surveyType !== "c" && (
                    <SurveySelectFour
                      id={question.id}
                      setAnswersList={setAnswersList}
                      answersList={answersList}
                      showImg={surveyType === "a"}
                      isSoleQuestion={currentScreen.questions.length === 1}
                      submit={submit}
                    />
                  )}
                {question.answerType === "DEGREE" &&
                  question.numDegree === 5 && (
                    <SurveySelectFive
                      id={question.id}
                      setAnswersList={setAnswersList}
                      answersList={answersList}
                      isSoleQuestion={currentScreen.questions.length === 1}
                      submit={submit}
                    />
                  )}
                {question.answerType === "DEGREE" &&
                  question.numDegree <= 4 &&
                  surveyType === "c" && (
                    <SurveySelectRange
                      id={question.id}
                      numDegree={question.numDegree}
                      minDegreeLabel={question.minDegreeLabel}
                      maxDegreeLabel={question.maxDegreeLabel}
                      setAnswersList={setAnswersList}
                      answersList={answersList}
                    />
                  )}
                {question.answerType === "DEGREE" &&
                  question.numDegree > 5 &&
                  surveyType === "c" && (
                    <SurveySelectRange
                      id={question.id}
                      numDegree={question.numDegree}
                      minDegreeLabel={question.minDegreeLabel}
                      maxDegreeLabel={question.maxDegreeLabel}
                      setAnswersList={setAnswersList}
                      answersList={answersList}
                    />
                  )}
                {question.answerType === "SHORT_TEXT" && (
                  <SurveyTextArea
                    id={question.id}
                    setAnswersList={setAnswersList}
                    answersList={answersList}
                  />
                )}
                {question.answerType === "MUL_CHOICE" && (
                  <MultipleChoice
                    questionId={question.id}
                    questionOptions={question.options}
                    maxAnsNum={question.maxAnsNum}
                    setAnswersList={setAnswersList}
                    answersList={answersList}
                    submit={submit}
                  />
                )}
                {question.footer && (
                  <p className="quest-desc">{question.footer}</p>
                )}
              </div>
            </div>
          </div>
        ))}
        <div className="link-buttons">
          <button onClick={back} className="link-btn" alt="뒤로 버튼">
            뒤로
          </button>
          <button
            onClick={submit}
            className="link-btn"
            alt={isLastScreenOfSurvey ? "완료하기 버튼" : "다음으로 버튼"}
            disabled={!isSubmitActive}
          >
            {isLastScreenOfSurvey ? "완료하기" : "다음으로"}
          </button>
        </div>
      </div>
    </div>
  );
};

function NextArrow({ currentSlide, slideCount, ...props }) {
  return <img src={files + "/left.svg"} alt="다음 슬라이드" {...props} />;
}

function PrevArrow({ currentSlide, slideCount, ...props }) {
  return <img src={files + "/left.svg"} alt="이전 슬라이드" {...props} />;
}

export default Surveys;
